import {
  Box,
  Chip,
  Stack,
  Typography,
  useTheme,
  Grid,
  Button,
} from "@mui/material";
import SvgIconStyle from "~src/components/SvgIconStyle";
import BrandAvatar from "~src/components/BrandAvatar";
import BrandCover from "~src/components/BrandCover";
import { followerCount, reviewsCount } from "~src/utils/brandHelpers";
import Iconify from "~src/components/Iconify";
import { FullBrand } from "~src/types/brand";
import { BrandCardChart } from "./BrandScoreChart";
import {colors} from "~src/data/brandTabColors.ts"
import GridRow from "./GridRow";
import Backdrop from "@mui/material/Backdrop";

export const BrandsTile = ({
  competitor,
  index,
  lock,
}: {
  competitor: FullBrand;
  index: number;
  lock: boolean;
}) => {
  const theme = useTheme();

  function extractYear(dateString: string | undefined): number | undefined {
    if (!dateString) {
      return undefined;
    }
    const date = new Date(dateString);
    return date.getUTCFullYear();
  }

  return (
    <Stack
      sx={{
        color: "text.primary",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
        minHeight: 750,
        border: 1,
        borderColor: colors[index],
        borderRadius: "12px",
        backgroundColor: theme.palette.common.white,
        overflow: "hidden",
        position: "relative",
        zIndex: 0,
      }}
      data-testid="trending-brands-grid"
    >
      {lock && (
        <Backdrop
          sx={{
            position: "absolute",
            zIndex: 1,
            color: "#fff",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            textAlign: "center",
          }}
          open={true}
        >
          <Box>
            <Typography sx={{ mb: 2 }}>
              Access additional competitors with the TrendRocket Pro plan
            </Typography>
            <Button variant="contained">Unlock with Pro</Button>
          </Box>
        </Backdrop>
      )}
      <Box sx={{ filter: lock ? "blur(3px)" : "" }}>
        <BrandCover
          brandName={competitor?.brandname || ''}
          coverImage={competitor?.coverimage}
          height="100px"
        />
        <Stack
          sx={{
            width: "100%",
            bottom: 20,
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              borderRadius: "100%",
              width: 102,
              height: 102,
              border: `4px solid ${theme.palette.common.white}`,
              alignItems: "center",
              mb: theme.spacing(2),
            }}
          >
            <BrandAvatar
              brandName={competitor?.brandname || ''}
              profileImage={competitor?.profileimage}
              size={85}
              sx={{
                border: `0.75px solid ${theme.palette.grey[300]}`,
              }}
            />
          </Box>
          <BrandCardTitle brand={competitor} />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              my: 3,
            }}
          >
            <BrandCardChart
              label="Scale"
              number={competitor?.scaling_score_ranking || 0}
            />
            <BrandCardChart
              label="Revenue"
              number={competitor?.revenue_score_ranking || 0}
            />
          </Stack>
          <BrandSocialIcons brand={competitor} />
          <Grid container spacing={1} sx={{
            mt: 2
          }}>
            <Grid 
              size={{ xs: 12 }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mx: 1,
                borderBottom: 1,
                borderColor: theme.palette.grey[300],
              }}
            >
              <Grid size={{ sm: 6 }}>
                <Typography sx={{ color: theme.palette.grey[500] }}>
                  Shopify Site
                </Typography>
              </Grid>
              <Grid size={{ sm: 6 }}
                sx={{
                  textAlign: "right",
                }}
              >
                <Button href={competitor.shopifysite} target="_blank">
                  <Iconify
                    icon={"ph:link-light"}
                    width={25}
                    height={25}
                    color={theme.palette.info.dark}
                  />
                </Button>
              </Grid>
            </Grid>
            <GridRow
              label="Year of launch"
              value={extractYear(competitor.brand_added)}
            />
            <GridRow label="Location" value={competitor.country} />
            <GridRow
              label="Website Traffic"
              value={competitor.web_traffic_growth?.last_month_traffic || "N/A"}
            />
            {competitor.store_data && (
              <GridRow
                label="Est. Monthly Revenue"
                value={competitor.store_data.estimated_monthly_revenue}
              />
            )}
            <GridRow
              label="IG Followers"
              value={competitor.instagram_followers}
            />
            <GridRow label="FB Likes" value={competitor.fblikes} />
            <GridRow
              label="FB Ads Active"
              value={
                <Iconify
                  width={20}
                  height={20}
                  icon={
                    competitor.is_fb_ad_active === 1
                      ? "mdi:tick-circle-outline"
                      : "charm:circle-cross"
                  }
                  color={
                    competitor.is_fb_ad_active === 1
                      ? theme.palette.success.main
                      : theme.palette.error.main
                  }
                />
              }
            />
            <GridRow label="FB Ads" value={competitor.fbads_count} />

            <GridRow
              label="Products Count"
              value={competitor.product_data?.count || "N/A"}
            />
            <GridRow
              label="Avg. Product Price"
              value={
                competitor.product_data?.avg_price !== undefined &&
                competitor.product_data?.avg_price !== null
                  ? competitor.product_data.avg_price.toFixed(2)
                  : "N/A"
              }
            />
          </Grid>
        </Stack>
      </Box>
    </Stack>
  );
};

const BrandCardTitle = ({ brand }: { brand: FullBrand | null }) => {
  return (
    <Stack
      spacing={1}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "75%",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          overflow: "hidden",
          whiteSpace: "normal",
          WebkitLineClamp: 2,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
        }}
      >
        {brand?.brandname}
      </Typography>
      <Stack direction="row" spacing={2}>
        <Typography
          variant="caption"
          sx={{
            color: "grey.400",
            textAlign: "center"
          }}>
          {followerCount(brand)} followers
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "grey.400",
            textAlign: "center"
          }}>
          {reviewsCount(brand)} reviews
        </Typography>
      </Stack>
      <Chip label={brand?.category_name} />
    </Stack>
  );
};

const BrandSocialIcons = ({ brand }: { brand: FullBrand | null }) => {
  return (
    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
      {brand?.brandsite && (
        <SvgIconStyle
          src="/assets/icons/social/website.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand?.fburl && (
        <SvgIconStyle
          src="/assets/icons/social/facebook.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand?.shopifysite && (
        <SvgIconStyle
          src="/assets/icons/social/shopify.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand?.instagramurl && (
        <SvgIconStyle
          src="/assets/icons/social/instagram.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
      {brand?.trustpilotsite && (
        <SvgIconStyle
          src="/assets/icons/social/trustpilot.svg"
          sx={{ width: 16, height: 16, opacity: 0.25 }}
        />
      )}
    </Stack>
  );
};
